import React from "react";
import _get from "lodash/get";
import _camelCase from "lodash/camelCase";
import ReactDOM from "react-dom";
import { parametrize, overrideStore } from "react-overridable";
import { SearchApp } from "@js/invenio_search_ui/components";
import { ListItemContainer } from "./ResultsList";
import { ActiveFilters } from "./ActiveFilters";
import { BucketAggregationValuesElement } from "./BucketAggregationValuesElement";
import { CountElement } from "./ResultCount";
import { EmptyResultsElement } from "./EmptyResultsElement";
import { ErrorElement } from "./ErrorElement";
import { SearchAppFacets } from "./SearchAppFacets";
import { SearchAppLayout } from "./SearchAppLayout";
import { ResultOptions } from "./ResultOptions";
import { SearchAppSearchbarContainer } from "./SearchAppSearchbarContainer";
import { Sort } from "./Sort";
import { ResultsPerPage } from "./ResultsPerPage";
import { SearchAppResults } from "./SearchAppResults";
import { FoldableBucketAggregationElement } from "./FoldableBucketAggregationElement";
import { ClearableSearchbarElement } from "./ClearableSearchbarElement";
import { RDMToggleComponent } from "@js/invenio_app_rdm/search/components";

export function parseSearchAppConfigs(
  configDataAttr = "invenio-search-config"
) {
  const searchAppRoots = [
    ...document.querySelectorAll(`[data-${configDataAttr}]`),
  ];

  return searchAppRoots.map((rootEl) => {
    const config = JSON.parse(rootEl.dataset[_camelCase(configDataAttr)]);
    return {
      rootEl,
      ...config,
    };
  });
}

export function createSearchAppsInit({
  componentOverrides = {},
  autoInit = true,
  ContainerComponent = React.Fragment,
} = {}) {
  const initSearchApp = async ({ rootEl, overridableIdPrefix, ...config }) => {
    const SearchAppSearchbarContainerWithConfig = parametrize(
      SearchAppSearchbarContainer,
      { appName: overridableIdPrefix }
    );

    const defaultComponents = {
      [`${overridableIdPrefix}.ActiveFilters.element`]: ActiveFilters,
      [`${overridableIdPrefix}.BucketAggregation.element`]:
        FoldableBucketAggregationElement,
      [`${overridableIdPrefix}.BucketAggregationValues.element`]:
        BucketAggregationValuesElement,
      [`${overridableIdPrefix}.Count.element`]: CountElement,
      [`${overridableIdPrefix}.EmptyResults.element`]: EmptyResultsElement,
      [`${overridableIdPrefix}.Error.element`]: ErrorElement,
      [`${overridableIdPrefix}.SearchApp.facets`]: SearchAppFacets,
      [`${overridableIdPrefix}.SearchApp.layout`]: SearchAppLayout,
      [`${overridableIdPrefix}.SearchApp.resultOptions`]: ResultOptions,
      [`${overridableIdPrefix}.ResultsPerPage.element`]: ResultsPerPage,
      [`${overridableIdPrefix}.SearchApp.searchbarContainer`]:
        SearchAppSearchbarContainerWithConfig,
      [`${overridableIdPrefix}.SearchFilters.Toggle.element`]:
        RDMToggleComponent,
      [`${overridableIdPrefix}.Sort.element`]: Sort,
      [`${overridableIdPrefix}.SearchApp.results`]: SearchAppResults,
      [`${overridableIdPrefix}.SearchBar.element`]: ClearableSearchbarElement,
      [`${overridableIdPrefix}.ResultsList.container`]: ListItemContainer,
    };
    // invenio search app just passes overrideStore.getAll to overridable registry,
    // so here we need to merge the 3 sources of overrides in order we need:
    const components = {
      ...defaultComponents,
      ...componentOverrides,
      ...overrideStore.getAll(),
    };

    for (const [id, component] of Object.entries(components)) {
      overrideStore.add(id, component);
    }

    ReactDOM.render(
      <ContainerComponent>
        <SearchApp
          config={config}
          // Use appName to namespace application components when overriding
          appName={overridableIdPrefix}
        />
      </ContainerComponent>,
      rootEl
    );
  };

  if (autoInit) {
    const searchAppConfigs = parseSearchAppConfigs();
    searchAppConfigs.forEach((config) => initSearchApp(config));
  } else {
    return initSearchApp;
  }
}

export const _getResultBuckets = (resultsAggregations, aggName) => {
  const thisAggs = _get(resultsAggregations, aggName, {});
  if ("buckets" in thisAggs) {
    if (!Array.isArray(thisAggs["buckets"])) {
      thisAggs["buckets"] = Object.entries(thisAggs["buckets"]).map(
        ([key, value]) => ({
          ...value,
          key,
        })
      );
    }
    return thisAggs["buckets"];
  }
  return [];
};

export const _getResultsStats = (resultsAggregations, aggName) => {
  const thisAggs = _get(resultsAggregations, aggName, {});
  return thisAggs.value;
};
